// Reading PRD attachments in the browser (RF-PRD-01). Text formats are sent as UTF-8,
// binary formats (PDF/DOCX) as base64. Nothing is executed; extraction happens server-side.
import type { ImportPrdInput } from "@maestros/contracts";

export const PRD_ACCEPT = ".md,.markdown,.txt,.pdf,.docx,text/markdown,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
/** Client-side guard; the server enforces its own configurable limit. */
export const PRD_MAX_BYTES = 10 * 1024 * 1024;

export type PrdKind = ImportPrdInput["source_kind"];

export function kindOf(file: File): PrdKind | null {
  const name = file.name.toLowerCase();
  if (name.endsWith(".md") || name.endsWith(".markdown")) return "markdown";
  if (name.endsWith(".txt")) return "txt";
  if (name.endsWith(".pdf")) return "pdf";
  if (name.endsWith(".docx")) return "docx";
  return null;
}

export class PrdFileError extends Error {
  constructor(
    readonly reason: "unsupported" | "too_large" | "empty" | "read_failed",
    message: string,
  ) {
    super(message);
  }
}

export interface ReadPrd {
  name: string;
  title: string;
  source_kind: PrdKind;
  content: string;
  content_encoding: "utf8" | "base64";
  size: number;
}

export function readPrdFile(file: File, onProgress?: (fraction: number) => void): Promise<ReadPrd> {
  const kind = kindOf(file);
  if (!kind) return Promise.reject(new PrdFileError("unsupported", file.name));
  if (file.size === 0) return Promise.reject(new PrdFileError("empty", file.name));
  if (file.size > PRD_MAX_BYTES) return Promise.reject(new PrdFileError("too_large", file.name));
  const binary = kind === "pdf" || kind === "docx";
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    reader.onerror = () => reject(new PrdFileError("read_failed", file.name));
    reader.onload = () => {
      const result = reader.result;
      let content = "";
      if (binary && typeof result === "string") content = result.slice(result.indexOf(",") + 1);
      else if (typeof result === "string") content = result;
      if (!content.trim()) {
        reject(new PrdFileError("empty", file.name));
        return;
      }
      onProgress?.(1);
      resolve({
        name: file.name,
        title: file.name.replace(/\.[^.]+$/, "").slice(0, 300) || file.name,
        source_kind: kind,
        content,
        content_encoding: binary ? "base64" : "utf8",
        size: file.size,
      });
    };
    if (binary) reader.readAsDataURL(file);
    else reader.readAsText(file);
  });
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
